import { OrderedMap } from 'immutable'
import {
  keyBy,
  some,
  isNull,
  zipObject,
  times,
  constant,
  get,
  reject,
} from 'lodash-es'

import { mergeReducers } from '../../../sharedLegacy/utils'
import createNamedSubscriptionsReducer from '../reducer.subscriptions'

import createNamedActions from './actions'

const INITIAL_PEOPLE_COUNT = 101
const defaultHeaderTabsKeys = ['everybody', 'identified', 'converted']

const getInitialState = (visibleTabList) => {
  const keysLength = visibleTabList.length
  return {
    selectedTab: visibleTabList[0], // first one is always selected
    objectId: null,
    isInfiniteLoading: false,
    ...zipObject(visibleTabList, times(keysLength, constant(OrderedMap()))),
    loadingStates: zipObject(
      visibleTabList,
      times(keysLength, constant('loading'))
    ),
    hasMoreElements: zipObject(
      visibleTabList,
      times(keysLength, constant(true))
    ),
    currentOffset: zipObject(visibleTabList, times(keysLength, constant(0))),
    orderBy: zipObject(visibleTabList, times(keysLength, constant(null))),
    order: zipObject(visibleTabList, times(keysLength, constant(null))),
  }
}

const hasViewedObject = (profile, objectId) => {
  return some(profile.timelines, { object: objectId.toString() })
}

const filterViewers = (profiles, objectId) => {
  return profiles.filter((profile) => hasViewedObject(profile, objectId))
}

const getRejectedViewers = (profiles, objectId) => {
  return reject(profiles, (profile) => hasViewedObject(profile, objectId))
}

const hadRecentViewingActivity = (profile, objectId) => {
  return some(profile.timelines, (timeline) => {
    return (
      timeline.object === objectId.toString() &&
      timeline.active &&
      timeline.engagement > 0
    )
  })
}

/** *
 * Expire profiles which last viewing activity is older than 60 seconds ago
 * @param profiles
 * @returns {*}
 */
const expireProfiles = (profiles, objectId) => {
  return profiles.filter((profile) =>
    hadRecentViewingActivity(profile, objectId)
  )
}

const getExpiredProfiles = (profiles, objectId) => {
  return reject(profiles, (profile) =>
    hadRecentViewingActivity(profile, objectId)
  )
}

const createNamedReducer = (namespace, reducerOptions = {}) => {
  const { actionNames } = createNamedActions(namespace)

  const visibleTabList = get(
    reducerOptions,
    'visibleTabList',
    defaultHeaderTabsKeys
  )
  const initialState = getInitialState(visibleTabList)

  const mainReducer = (state = initialState, action = undefined) => {
    switch (action.type) {
      case actionNames.INSET_INIT: {
        return {
          ...state,
          selectedTab: action.tab,
          objectId: action.objectId || null,
        }
      }
      case actionNames.INSET_TAB_ROUTE_CHANGE: {
        return { ...state, selectedTab: action.tab || 'everybody' }
      }
      case actionNames.INSET_AUDIENCE_FETCH_SUCCESS: {
        let { profiles } = action.data
        const hasMoreElements = profiles.length === INITIAL_PEOPLE_COUNT

        // fetch 101, display 100 so we always have
        // something to infinite load
        if (hasMoreElements) {
          profiles.pop()
        }

        if (reducerOptions.expireProfiles) {
          profiles = expireProfiles(profiles, state.objectId)
        } else if (reducerOptions.filterViewersOnly) {
          profiles = filterViewers(profiles, state.objectId)
        }

        const profileMap = OrderedMap(keyBy(profiles, 'uuid'))

        return {
          ...state,
          [action.tab]: profileMap,
          loadingStates: { ...state.loadingStates, [action.tab]: 'loaded' },
          hasMoreElements: {
            ...state.hasMoreElements,
            [action.tab]: hasMoreElements,
          },
          isInfiniteLoading: false,
          currentOffset: {
            ...state.currentOffset,
            [action.tab]: 0,
          },
        }
      }
      case actionNames.INSET_AUDIENCE_FETCH_FAILURE: {
        return {
          ...state,
          loadingStates: {
            ...state.loadingStates,
            [action.tab]: 'failed',
          },
        }
      }
      case actionNames.INSET_PROFILES_REFETCH_SUCCESS: {
        const { profiles } = action.data
        let toBeRemovedProfiles

        if (reducerOptions.expireProfiles) {
          toBeRemovedProfiles = getExpiredProfiles(profiles, state.objectId)
        } else if (reducerOptions.filterViewersOnly) {
          toBeRemovedProfiles = getRejectedViewers(profiles, state.objectId)
        }

        const newProfiles = toBeRemovedProfiles.reduce(
          (acc, val) => acc.delete(val.uuid),
          state[action.tab]
        )

        return {
          ...state,
          [action.tab]: newProfiles,
        }
      }
      case actionNames.INSET_INFINITE_LOAD: {
        return {
          ...state,
          isInfiniteLoading: true,
        }
      }
      case actionNames.INSET_INFINITE_LOAD_SUCCESS: {
        const { profiles } = action.data
        const hasMoreElements = profiles.length === INITIAL_PEOPLE_COUNT

        // fetch 101, display 100 so we always have
        // something to infinite load
        if (hasMoreElements) {
          profiles.pop()
        }

        return {
          ...state,
          isInfiniteLoading: false,
          hasMoreElements: {
            ...state.hasMoreElements,
            [action.tab]: hasMoreElements,
          },
          currentOffset: {
            ...state.currentOffset,
            [action.tab]: action.newOffset,
          },
          [action.tab]: state[action.tab].concat(keyBy(profiles, 'uuid')),
        }
      }
      case actionNames.INSET_INFINITE_LOAD_ERROR: {
        return { ...state, isInfiniteLoading: false }
      }
      case actionNames.INSET_REALTIME_DATA_UPDATE: {
        const profile = action.realtimeData
        const currentProfiles = state[state.selectedTab]
        let newProfiles = currentProfiles

        if (currentProfiles.has(profile.uuid)) {
          if (
            reducerOptions.expireProfiles &&
            !hadRecentViewingActivity(profile, state.objectId)
          ) {
            // expire profile due to inactivity
            newProfiles = currentProfiles.remove(profile.uuid)
          } else {
            newProfiles = currentProfiles.set(profile.uuid, profile)
          }
        } else if (reducerOptions.addNewRealtimeProfiles) {
          if (
            isNull(state.objectId) ||
            hasViewedObject(profile, state.objectId)
          ) {
            if (
              reducerOptions.expireProfiles &&
              !hadRecentViewingActivity(profile, state.objectId)
            ) {
              // do not include profile due to inactivity
              // (realtime update was about something else)
              return state
            }

            newProfiles = OrderedMap({ [profile.uuid]: profile }).concat(
              currentProfiles
            )
          }
        }

        return {
          ...state,
          [state.selectedTab]: newProfiles,
        }
      }
      case actionNames.INSET_LEAVE: {
        return {
          ...initialState,
        }
      }
      case actionNames.INSET_ORDER_CHANGE: {
        const tab = state.selectedTab
        return {
          ...state,
          currentOffset: { ...state.currentOffset, [tab]: 0 },
          orderBy: { ...state.orderBy, [tab]: action.orderBy },
          order: { ...state.order, [tab]: action.order },
        }
      }
      case actionNames.INSET_REMOVE_ATTENDEE: {
        const { uuid } = action
        const profiles = {}

        visibleTabList.forEach((tab) => {
          const attendees = state[tab]

          if (attendees.has(uuid)) {
            profiles[tab] = attendees.delete(uuid)
          }
        })

        return {
          ...state,
          ...profiles,
        }
      }
      default:
        return state
    }
  }

  const subscriptionsReducer = createNamedSubscriptionsReducer(namespace)

  return mergeReducers(mainReducer, subscriptionsReducer)
}

export default createNamedReducer
